import React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, TextField } from "@mui/material";
import { toast } from "react-toastify";
import organizationservice from "../../../service/organizationservice";
import errorHandler from "../../../utils/errorHandler";

const FirsBusinessLimit = ({ organizationId }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [limit, setLimit] = React.useState("1");
  const [isUnlimited, setIsUnlimited] = React.useState(false);

  const businessesQuery = useQuery({
    queryKey: ["firs-businesses", organizationId],
    enabled: !!organizationId,
    queryFn: () => organizationservice.getFirsBusinesses(organizationId),
  });

  const result = businessesQuery.data?.data?.data;
  const businesses = result?.businesses ?? [];
  const maxBusinesses = result?.maxBusinesses;

  const limitMutation = useMutation({
    mutationFn: () => organizationservice.updateFirsBusinessLimit(organizationId, isUnlimited ? null : Number(limit)),
    onError: ({ response }) => errorHandler(response),
    onSuccess: ({ data }) => {
      toast.success("Business limit updated successfully.");
      if (data?.data?.testSynced === false) toast.warning("The limit was saved but test mode could not be updated.");
      setIsOpen(false);
      businessesQuery.refetch();
    },
  });

  const removeMutation = useMutation({
    mutationFn: (businessId) => organizationservice.removeFirsBusiness(organizationId, businessId),
    onError: ({ response }) => errorHandler(response),
    onSuccess: () => {
      toast.success("Business removed successfully.");
      businessesQuery.refetch();
    },
  });

  function handleOpen() {
    setIsUnlimited(maxBusinesses === null);
    setLimit(String(maxBusinesses ?? 1));
    setIsOpen(true);
  }

  const isLimitValid = isUnlimited || (Number.isInteger(Number(limit)) && Number(limit) >= 1);

  return (
    <React.Fragment>
      <div className="mb-2 fs-sm d-flex align-items-center">
        <span className="text-secondary me-2">NRS Businesses:</span>
        <span className="flex-grow-1">
          {businesses.length} of {maxBusinesses === null ? "Unlimited" : maxBusinesses ?? "-"}
        </span>
        <Button size="small" variant="text" disabled={businessesQuery.isPending} onClick={handleOpen}>
          Manage
        </Button>
      </div>

      <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>NRS Businesses</DialogTitle>
        <Divider />
        <DialogContent>
          <FormControlLabel
            control={<Checkbox checked={isUnlimited} onChange={(e) => setIsUnlimited(e.target.checked)} />}
            label="Unlimited"
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Business limit"
            value={limit}
            disabled={isUnlimited}
            inputProps={{ min: 1 }}
            helperText="Lowering the limit keeps current businesses and only blocks new ones."
            onChange={(e) => setLimit(e.target.value)}
            sx={{ my: 2 }}
          />
          <Divider />
          {businesses.length === 0 ? (
            <p className="fs-sm text-secondary mt-3 mb-0">No businesses in use.</p>
          ) : (
            businesses.map((business) => (
              <div key={business.businessId} className="d-flex justify-content-between align-items-center gap-3 py-2 border-bottom">
                <div className="fs-sm">
                  <div>{business.businessId}</div>
                  <div className="text-secondary">TIN: {business.tin ?? "-"}</div>
                </div>
                <Button
                  size="small"
                  color="error"
                  disabled={removeMutation.isPending}
                  onClick={() => removeMutation.mutate(business.businessId)}
                >
                  Remove
                </Button>
              </div>
            ))
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button color="inherit" onClick={() => setIsOpen(false)}>
            Close
          </Button>
          <Button variant="contained" disabled={!isLimitValid || limitMutation.isPending} onClick={() => limitMutation.mutate()}>
            {limitMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default FirsBusinessLimit;
